/**
 * MonthlyReportPage — W2.2 + F037 + v2-Cutover
 *
 * Seite fuer den monatlichen Reward-Report (FR29, FR30).
 * Unterstuetzt Perioden-Navigation, URL-Query-Param, PDF-Export via window.print().
 * Premium-Guard bei 402-Response.
 *
 * F037 / v2-Cutover:
 * - CSV-Downloads treffen /api/v2/reports/* mit Bearer-Auth (fetch+blob, kein window.open)
 * - Timezone fuer CSV/Versand: automatisch aus dem Browser-Wert abgeleitet, kein
 *   sichtbarer Filter mehr (P1 Design-Audit 2026-09-01 — siehe unten)
 * - SendTestEmailButton nutzt useSendMonthlyReports (bereits v2), da kein dedizierter
 *   v2 Test-Mail-Endpoint existiert (/reportings/deliver_email war Monolith-Session-Route)
 *
 * P1 (Design-Audit 2026-09-01): der sichtbare Kontakt-/Zeitzonen-Filter unter
 * dem Monats-Picker (FE-9) ist entfernt — die Design-Vorlage
 * (monthly-report.html) kennt nur Monat + PDF im Header. Die Kontakt-Filter-
 * UI (contact_id-Scoping des gesamten Reports) entfaellt damit vollstaendig;
 * die Zeitzone bleibt im Versand-Dialog (SendReportDialog) wählbar.
 */
import './monthly-report.css'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Mail,
  FileSpreadsheet,
  Send,
  Rocket,
} from 'lucide-react'
import { useMonthlyReport } from './useMonthlyReport'
import { useAccount } from '../settings/useAccount'
import { useAccountMutations } from '../settings/useAccountMutations'
import { useSendMonthlyReports } from './useSendMonthlyReports'
import { useAvailableMonths, useTimeZones } from './useReportMeta'
import { SendReportDialog } from './SendReportDialog'
import { useAuthStore } from '../../stores/authStore'
import { ReportHero } from './ReportHero'
import { HotspotRewardTable } from './HotspotRewardTable'
import { DailyRewardsChart } from './DailyRewardsChart'
import { StaticDailyRewardsChart } from './StaticDailyRewardsChart'
import { PremiumRequired } from '../../components/PremiumRequired'
import { RewardDataGapNotice } from '../../components/RewardDataGapNotice'
import { isPeriodInRewardDataGap, isYearInRewardDataGap } from '../../lib/rewardDataGap'
import { apiErrorMessage, apiErrorStatus } from '../../lib/errors/apiErrorMessage'
import {
  prevMonth,
  nextMonth,
  isFuturePeriod,
  periodLabel,
  monthLabel,
  defaultPeriod,
  isValidPeriod,
  downloadCsvV2,
  downloadTaxYearlyCsv,
  nextPaint,
  formatCurrency,
} from './reportUtils'
import type { CsvFormat } from './reportUtils'

// --- Timezone-Hilfsfunktionen ---

function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// --- Loading Skeleton ---

function ReportSkeleton() {
  const { t } = useTranslation('reports')
  return (
    <div aria-busy="true" aria-label={t('skeleton.loading')}>
      <div className="report-skeleton-hero" />
      <div className="report-skeleton-table">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="report-skeleton-row" aria-hidden="true" />
        ))}
      </div>
    </div>
  )
}

// --- Error State ---

type ErrorStateProps = { onRetry: () => void }

function ErrorState({ onRetry }: ErrorStateProps) {
  const { t } = useTranslation('reports')
  return (
    <div className="report-error-state" role="alert">
      <p>{t('error.loadFailed')}</p>
      <button className="btn btn-primary" onClick={onRetry} type="button">
        {t('error.retry')}
      </button>
    </div>
  )
}

// --- Empty State ---

type EmptyStateProps = { label: string }

function EmptyState({ label }: EmptyStateProps) {
  const { t } = useTranslation('reports')
  return (
    <div className="report-empty-state" role="status">
      {t('empty.noData', { label })}
    </div>
  )
}

// --- Invalid-Period State (Finding: isInvalidPeriod wurde nie gerendert) ---
//
// useMonthlyReport liefert isInvalidPeriod bei BE 422 (z.B. future_period oder
// falsches Format). Vorher wurde dieses Flag von der Seite nirgends
// destrukturiert/gerendert — der Inhaltsbereich blieb bei einer per URL
// manuell gesetzten Zukunfts-/ungueltigen Periode komplett leer, ohne
// Loading-, Error- oder Empty-Hinweis.

function InvalidPeriodState() {
  const { t } = useTranslation('reports')
  return (
    <div className="report-empty-state" role="status">
      <p style={{ fontWeight: 500, marginBottom: 4 }}>{t('invalidPeriod.title')}</p>
      <p className="muted">{t('invalidPeriod.message')}</p>
    </div>
  )
}

// --- PDF Export Button ---
//
// F-R3-2 (Performance): Die PDF-Erzeugung laeuft rein clientseitig ueber
// window.print() und blockiert den Main-Thread 15-25s. Bisher gab es dafuer
// keinerlei Rueckmeldung — der Tab wirkte abgestuerzt. Der Export setzt jetzt
// erst einen Lade-State, wartet einen Paint ab (nextPaint) und startet dann
// erst die Generierung; alle Export-Buttons sind so lange deaktiviert.

type PdfExportButtonProps = {
  isExporting: boolean
  disabled?: boolean
  onExportingChange: (isExporting: boolean) => void
}

function PdfExportButton({ isExporting, disabled, onExportingChange }: PdfExportButtonProps) {
  const { t } = useTranslation('reports')

  async function handlePrint() {
    onExportingChange(true)
    try {
      // Erst rendern lassen, dann blockieren — sonst sieht niemand den Indikator.
      await nextPaint()
      window.print()
    } finally {
      onExportingChange(false)
    }
  }

  return (
    <button
      className="btn btn-sm btn-primary"
      onClick={handlePrint}
      type="button"
      aria-label={t('pdf.ariaLabel')}
      disabled={isExporting || disabled}
      aria-busy={isExporting}
    >
      <Download size={14} aria-hidden="true" />
      {isExporting ? t('pdf.exporting') : t('pdf.export')}
    </button>
  )
}

// --- CSV Export (F037 / v2-Cutover, P2 Design-Audit 2026-09-01) ---
//
// P2: Die Vorlage kombiniert einen Format-Select mit einem einzelnen
// "Download CSV"-Button statt eines Dropdown-Menu-Buttons — Komposition
// entsprechend angeglichen. "standard" (Fleet & Tax Monthly Statement, ein
// bereits bestehender api/v2-Endpoint) ergaenzt die 5 Formate aus
// ACCEPTANCE.md.

const CSV_FORMATS: CsvFormat[] = ['standard', 'horizontal', 'vertical', 'advanced', 'koinly']

type CsvExportControlsProps = {
  period: string
  timeZone?: string
  /** F-R3-2: gesperrt, solange ein anderer Export laeuft. */
  disabled?: boolean
  onDownloadingChange?: (isDownloading: boolean) => void
}

function CsvExportControls({
  period,
  timeZone,
  disabled,
  onDownloadingChange,
}: CsvExportControlsProps) {
  const { t } = useTranslation('reports')
  const [format, setFormat] = useState<CsvFormat>(CSV_FORMATS[0])
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // F-R3-1: Der Demo-Token wird in downloadCsvV2 aufgeloest (resolveCsvAuthToken)
  // — hier reicht der regulaere Session-Token als Fallback.
  const token = useAuthStore((s) => s.accessToken)

  async function handleExport() {
    setIsDownloading(true)
    onDownloadingChange?.(true)
    setError(null)
    try {
      await downloadCsvV2(format, period, token, undefined, timeZone)
    } catch (e) {
      // Der CSV-Endpoint antwortet seit R3 bei Monaten ausserhalb des erlaubten
      // Fensters mit 402 — das ist kein stiller Fehlschlag mehr.
      setError(
        apiErrorStatus(e) === 402
          ? t('csv.outOfWindow')
          : apiErrorMessage(e, t('csv.downloadError')),
      )
    } finally {
      setIsDownloading(false)
      onDownloadingChange?.(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <select
        className="report-filter-select"
        value={format}
        onChange={(e) => setFormat(e.target.value as CsvFormat)}
        aria-label={t('csv.formatAriaLabel')}
        disabled={isDownloading || disabled}
      >
        {CSV_FORMATS.map((f) => (
          <option key={f} value={f}>
            {t(`csv.formats.${f}`)}
          </option>
        ))}
      </select>
      <button
        className="btn btn-sm btn-secondary"
        onClick={handleExport}
        type="button"
        aria-label={t('csv.ariaLabel')}
        disabled={isDownloading || disabled}
        aria-busy={isDownloading}
      >
        <FileSpreadsheet size={14} aria-hidden="true" />
        {isDownloading ? t('csv.downloading') : t('csv.label')}
      </button>
      {error && (
        <span role="alert" className="text-danger" style={{ fontSize: 12 }}>
          {error}
        </span>
      )}
    </div>
  )
}

// --- Issue #95: Steuer-Jahresexport (eigene Achse: Jahr statt Monat, daher
// kein Teil von CsvExportControls / CSV_FORMATS). ---

function taxYearlyOptions(): number[] {
  const currentYear = new Date().getFullYear()
  return [currentYear, currentYear - 1, currentYear - 2]
}

function TaxYearlyExportControls() {
  const { t } = useTranslation('reports')
  const years = useMemo(() => taxYearlyOptions(), [])
  const [year, setYear] = useState<number>(years[1] ?? years[0])
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const token = useAuthStore((s) => s.accessToken)
  const isDataGap = isYearInRewardDataGap(year)

  async function handleExport() {
    setIsDownloading(true)
    setError(null)
    try {
      await downloadTaxYearlyCsv(year, token)
    } catch (e) {
      setError(
        apiErrorStatus(e) === 402
          ? t('taxYearly.outOfWindow')
          : apiErrorMessage(e, t('csv.downloadError')),
      )
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <select
        className="report-filter-select"
        value={year}
        onChange={(e) => setYear(Number(e.target.value))}
        aria-label={t('taxYearly.yearAriaLabel')}
        disabled={isDownloading}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <button
        className="btn btn-sm btn-secondary"
        onClick={handleExport}
        type="button"
        aria-label={t('taxYearly.ariaLabel')}
        disabled={isDownloading}
        aria-busy={isDownloading}
      >
        <FileSpreadsheet size={14} aria-hidden="true" />
        {isDownloading ? t('csv.downloading') : t('taxYearly.label')}
      </button>
      {isDataGap && <RewardDataGapNotice variant="export" />}
      {error && (
        <span role="alert" className="text-danger" style={{ fontSize: 12 }}>
          {error}
        </span>
      )}
    </div>
  )
}

// --- Auto-Email Setting (F037) ---

function AutoEmailToggle() {
  const { t } = useTranslation('reports')
  const { account } = useAccount()
  const { updateSettings } = useAccountMutations()
  const [isUpdating, setIsUpdating] = useState(false)

  const isEnabled = account?.monthly_report ?? false

  async function handleToggle() {
    setIsUpdating(true)
    try {
      await updateSettings({ monthly_report: !isEnabled })
    } finally {
      setIsUpdating(false)
    }
  }

  // FE-5: v1 (views/reports/index.haml:33-41) erklaert den Status im Klartext,
  // v2 hatte nur die nackte Checkbox.
  return (
    <div className="auto-email-block">
      <label className="auto-email-toggle">
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={handleToggle}
          disabled={isUpdating}
          aria-label={t('autoEmail.ariaLabel')}
        />
        <span className="toggle-label">
          <Mail size={14} aria-hidden="true" />
          {t('autoEmail.label')}
        </span>
      </label>
      <p className="muted auto-email-hint" role="status">
        {isEnabled ? t('autoEmail.enabledHint') : t('autoEmail.disabledHint')}
      </p>
    </div>
  )
}

// --- S10-05: Send Now Button — triggers immediate bulk email send ---

function SendNowButton() {
  const { t } = useTranslation('reports')
  const [isSending, setIsSending] = useState(false)
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const { sendNow } = useSendMonthlyReports()

  async function handleSendNow() {
    // S10-05: Confirm dialog before sending
    if (!window.confirm(t('sendNow.confirm'))) {
      return
    }
    setIsSending(true)
    setResult(null)
    try {
      const outcome = await sendNow()
      if (outcome.ok) {
        setResult({ type: 'success', message: outcome.message })
      } else {
        setResult({ type: 'error', message: outcome.error })
      }
      setTimeout(() => setResult(null), 5000)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <>
      <button
        className="btn btn-sm"
        onClick={handleSendNow}
        disabled={isSending}
        type="button"
        aria-label={t('sendNow.ariaLabel')}
        title={t('sendNow.title')}
      >
        <Rocket size={14} aria-hidden="true" />
        {isSending ? t('sendNow.sending') : t('sendNow.buttonLabel')}
      </button>
      {result && (
        <span
          role="status"
          aria-live="polite"
          className={result.type === 'success' ? 'muted' : 'text-danger'}
          style={{ fontSize: 12, marginLeft: 8 }}
        >
          {result.message}
        </span>
      )}
    </>
  )
}

// --- Manueller Versand / Empfaenger bearbeiten (R3/FE-4) ---
//
// Oeffnet den Versand-Dialog (Monat, Zeitzone, Empfaenger frei wählbar).
// v1: "Manually send email reports" -> views/reports/email.haml.
//
// P2 (Design-Audit 2026-09-01): stand vorher als zweiter, gleichrangiger
// "Send email reports"-Button neben SendNowButton — wirkte redundant. Die
// Vorlage kennt nur EINEN Send-Button ("Send now") plus einen Ghost-Link
// ("Edit recipients"). SendNowButton bleibt der primaere Send-Button (sendet
// sofort an alle Kontakte mit aktiviertem Monatsbericht); dieser hier wird
// zum sekundaeren "Empfaenger bearbeiten"-Link, der denselben Dialog oeffnet
// — der Dialog erlaubt Empfaenger-Auswahl UND Versand, Funktionalität
// bleibt vollstaendig erhalten.

type SendReportButtonProps = {
  period: string
  timeZone: string
}

function SendReportButton({ period, timeZone }: SendReportButtonProps) {
  const { t } = useTranslation('reports')
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        className="btn btn-sm btn-ghost"
        onClick={() => setIsOpen(true)}
        type="button"
        aria-label={t('sendDialog.openAriaLabel')}
      >
        <Send size={14} aria-hidden="true" />
        {t('sendDialog.open')}
      </button>
      {isOpen && (
        <SendReportDialog
          onClose={() => setIsOpen(false)}
          initialMonth={period}
          initialTimeZone={timeZone}
        />
      )}
    </>
  )
}

// --- Period Selector (R3/FE-1: Monats-Dropdown wie v1 + Pfeile als Komfort) ---

type PeriodSelectorProps = {
  period: string
  months: { value: string; label: string }[]
  onPrev: () => void
  onNext: () => void
  onSelect: (period: string) => void
}

function PeriodSelector({ period, months, onPrev, onNext, onSelect }: PeriodSelectorProps) {
  const { t } = useTranslation('reports')
  const allowed = months.map((m) => m.value)
  // Grenzen kommen ausschliesslich vom BE (available_months). Ist die Liste
  // (noch) leer, faellt die Navigation auf die alte Zeitlogik zurueck.
  const nextPeriod = nextMonth(period)
  const prevPeriod = prevMonth(period)
  const isForwardDisabled =
    allowed.length > 0 ? !allowed.includes(nextPeriod) : isFuturePeriod(nextPeriod)
  const isBackDisabled = allowed.length > 0 ? !allowed.includes(prevPeriod) : false
  const hasCurrent = allowed.includes(period)

  // F-R3-3: Der Zurueck-Pfeil war fuer Free-/abgelaufene Accounts stumm inert —
  // ohne Tooltip und ohne Erklaerung, warum aeltere Monate nicht erreichbar sind.
  const backLabel = isBackDisabled ? t('period.prevLocked') : t('period.prev')

  return (
    <div className="period-selector" role="group" aria-label={t('period.navigation')}>
      <button
        className="btn btn-sm btn-icon"
        onClick={onPrev}
        disabled={isBackDisabled}
        type="button"
        aria-label={backLabel}
        title={backLabel}
        aria-disabled={isBackDisabled}
      >
        <ChevronLeft size={16} aria-hidden="true" />
      </button>
      <select
        className="report-filter-select period-select"
        value={period}
        onChange={(e) => onSelect(e.target.value)}
        aria-label={t('period.selectMonth')}
        disabled={months.length === 0}
      >
        {!hasCurrent && <option value={period}>{periodLabel(period)}</option>}
        {months.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
      <button
        className="btn btn-sm btn-icon"
        onClick={onNext}
        disabled={isForwardDisabled}
        type="button"
        aria-label={t('period.next')}
        aria-disabled={isForwardDisabled}
      >
        <ChevronRight size={16} aria-hidden="true" />
      </button>
      {isBackDisabled && (
        <p className="muted period-history-hint" data-testid="period-history-hint">
          {t('period.historyHint')} <Link to="/plans">{t('period.historyHintLink')}</Link>
        </p>
      )}
    </div>
  )
}

// --- Free-Tier-Upsell (R3/FE-7) ---
//
// v1 (reports_controller.rb:49-53) sperrt Free-User NICHT komplett aus, sondern
// begrenzt sie auf den laufenden Monat und zeigt einen Upsell-Hinweis. Das v2-FE
// rendert bei 402 bislang die harte PremiumRequired-Karte.

type FreeTierNoticeProps = {
  onCurrentMonth: () => void
}

function FreeTierNotice({ onCurrentMonth }: FreeTierNoticeProps) {
  const { t } = useTranslation('reports')
  return (
    <div className="report-empty-state" role="status">
      <p style={{ fontWeight: 500, marginBottom: 4 }}>{t('freeTier.currentMonthOnly')}</p>
      <p className="muted">{t('freeTier.upgradeCta')}</p>
      <button className="btn btn-sm btn-primary" type="button" onClick={onCurrentMonth}>
        {t('freeTier.showCurrentMonth')}
      </button>
    </div>
  )
}

// --- Ausserhalb des verfuegbaren Zeitraums (R3-Review F5) ---
//
// Ein 402 heisst nicht automatisch "kein Premium": auch ein Premium-Account
// bekommt 402, wenn der angefragte Monat aelter ist als das erlaubte Fenster
// (max_months, i.d.R. 24). Vorher landete dieser Fall in der Paywall — fuer
// zahlende Nutzer schlicht falsch. `max_months > 0` markiert diesen Zustand.

type OutOfWindowNoticeProps = {
  maxMonths: number
  latestMonth?: string
  onLatestMonth: () => void
}

function OutOfWindowNotice({ maxMonths, latestMonth, onLatestMonth }: OutOfWindowNoticeProps) {
  const { t } = useTranslation('reports')
  return (
    <div className="report-empty-state" role="status" data-testid="report-out-of-window">
      <p style={{ fontWeight: 500, marginBottom: 4 }}>{t('outOfWindow.title')}</p>
      <p className="muted">{t('outOfWindow.message', { months: maxMonths })}</p>
      {latestMonth && (
        <button className="btn btn-sm btn-primary" type="button" onClick={onLatestMonth}>
          {t('outOfWindow.showLatest', { label: periodLabel(latestMonth) })}
        </button>
      )}
    </div>
  )
}

// --- Main Page ---

type Props = {
  period: string
  onPeriodChange: (period: string) => void
}

export function MonthlyReportPage({ period, onPeriodChange }: Props) {
  const { t } = useTranslation('reports')
  // F-R3-2: Ein laufender Export sperrt beide Export-Buttons und zeigt einen
  // Statustext — die PDF-Erzeugung blockiert den Tab mehrere Sekunden.
  const [isPdfExporting, setIsPdfExporting] = useState(false)
  const [isCsvDownloading, setIsCsvDownloading] = useState(false)
  const { months, defaultMonth } = useAvailableMonths()
  const { timeZones, defaultZone } = useTimeZones()

  // P1 (Design-Audit 2026-09-01): Kein sichtbarer Kontakt-/Zeitzonen-Filter
  // mehr (die Vorlage kennt nur Monat + PDF im Header) — die Zeitzone fuer
  // CSV-Export/Versand wird weiterhin automatisch aus dem Browser-Wert
  // abgeleitet und, sofern moeglich, auf den Rails-Zonennamen (z.B. "Berlin")
  // gemappt. Eine manuelle Auswahl bleibt im Versand-Dialog moeglich.
  const timeZone = useMemo(() => {
    const browserTz = browserTimeZone()
    if (timeZones.length === 0) return browserTz
    const byIdentifier = timeZones.find((tz) => tz.identifier === browserTz)
    if (byIdentifier) return byIdentifier.name
    return defaultZone || browserTz
  }, [timeZones, defaultZone])

  const {
    report,
    isLoading,
    isError,
    isPremiumRequired,
    isInvalidPeriod,
    maxMonths,
    availableMonths,
    isFetching,
    isPlaceholderData,
    refetch,
  } = useMonthlyReport(period)

  const label = periodLabel(period)
  // FE-7: max_months === 0 markiert den Free-Pfad (nur laufender Monat) — hier
  // gilt v1-Verhalten (Upsell-Hinweis), NICHT die harte Premium-Sperre.
  const isFreeTierBlocked = isPremiumRequired && maxMonths === 0
  // R3-Review F5: max_months > 0 = Premium, aber Monat ausserhalb des Fensters.
  const outOfWindowMax =
    isPremiumRequired && maxMonths !== undefined && maxMonths > 0 ? maxMonths : undefined
  const showPremiumGate = isPremiumRequired && !isFreeTierBlocked && outOfWindowMax === undefined
  const currentMonth = defaultMonth ?? months[0]?.value
  // Neuester erlaubter Monat: bevorzugt aus dem 402-Body (Sortierung dort nicht
  // garantiert, daher Maximum ueber die sortierbaren "YYYY-MM"-Strings).
  const latestAvailableMonth =
    (availableMonths ?? []).reduce<string | undefined>(
      (max, m) => (max === undefined || m > max ? m : max),
      undefined,
    ) ?? currentMonth
  // R3-Review F4: placeholderData zeigt beim Monatswechsel die Zahlen der
  // VORHERIGEN Periode unter dem neuen Label — bei steuerrelevanten Betraegen
  // muss das sichtbar sein.
  const isStale = isPlaceholderData && isFetching

  // R3-Review F6: Monatslabels aus dem BE sind immer englisch — clientseitig in
  // der aktiven Sprache formatieren, BE-Label nur als Fallback.
  const localizedMonths = useMemo(
    () => months.map((m) => ({ value: m.value, label: monthLabel(m.value, m.label) })),
    [months],
  )

  function handlePrev() {
    onPeriodChange(prevMonth(period))
  }

  function handleNext() {
    if (!isFuturePeriod(nextMonth(period))) {
      onPeriodChange(nextMonth(period))
    }
  }

  return (
    <div className="monthly-report-page">
      <div className="section-header" style={{ marginBottom: 20 }}>
        <div>
          <h1>{t('page.title')}</h1>
        </div>
        <div className="export-actions">
          {/* R3-Review P1-4 / P1 (Design-Audit 2026-09-01): schlanker Header — Periode
              + PDF in EINER Zeile, wie in der Design-Vorlage. Der CSV-Export zieht in
              die "Export"-Karte der rechten Spalte um (siehe unten), Funktionalität
              unveraendert. Die Perioden-Navigation stand vorher als eigener Block
              unterhalb der Kontakt-/Zeitzonen-Filterzeile — das ergab einen zweiten,
              redundanten Monats-Navigator statt eines schlanken Headers. */}
          <PeriodSelector
            period={period}
            months={localizedMonths}
            onPrev={handlePrev}
            onNext={handleNext}
            onSelect={onPeriodChange}
          />
          <PdfExportButton
            isExporting={isPdfExporting}
            disabled={isCsvDownloading}
            onExportingChange={setIsPdfExporting}
          />
          {isPdfExporting && (
            <span
              role="status"
              aria-live="polite"
              className="muted export-progress-hint"
              data-testid="pdf-export-progress"
            >
              {t('pdf.progressHint')}
            </span>
          )}
        </div>
      </div>

      {isFreeTierBlocked && (
        <FreeTierNotice onCurrentMonth={() => currentMonth && onPeriodChange(currentMonth)} />
      )}

      {outOfWindowMax !== undefined && (
        <OutOfWindowNotice
          maxMonths={outOfWindowMax}
          latestMonth={latestAvailableMonth}
          onLatestMonth={() => latestAvailableMonth && onPeriodChange(latestAvailableMonth)}
        />
      )}

      {/* Demo-Start bleibt auf /monthly-report: Report-Query invalidieren + neu laden,
          statt hart nach /dashboard zu navigieren. */}
      {showPremiumGate && (
        <PremiumRequired onDemoStart={refetch} demoQueryKey={['reports', 'monthly']} />
      )}

      {/* R3-Review P1-4 (Design-Audit): 2fr/1fr-Layout wie die Vorlage — Bericht
          links, Export/Email-Karten rechts. Die rechte Spalte ist bewusst NICHT an
          den Ladezustand des Reports gebunden (E-Mail-Versand/Auto-Report sind
          Konto-Einstellungen, kein Report-Inhalt) und bleibt daher immer sichtbar,
          solange keine der drei Sperr-/Upsell-Ansichten oben aktiv ist. */}
      {!showPremiumGate && !isFreeTierBlocked && outOfWindowMax === undefined && (
        <div className="monthly-report-grid">
          <div className="monthly-report-col-main">
            {!isPremiumRequired && isInvalidPeriod && <InvalidPeriodState />}

            {!isPremiumRequired && !isInvalidPeriod && isLoading && <ReportSkeleton />}

            {!isPremiumRequired && !isInvalidPeriod && isError && !isLoading && (
              <ErrorState onRetry={refetch} />
            )}

            {!isPremiumRequired && !isInvalidPeriod && !isLoading && !isError && report && (
              <div className={isStale ? 'report-content is-stale' : 'report-content'}>
                {/* R3-Review F4: solange placeholder-Daten sichtbar sind, liegt ein
                    Overlay ueber den Zahlen — sonst liest man die Betraege des
                    vorherigen Monats unter dem neuen Monatslabel. */}
                {isStale && (
                  <div
                    className="report-stale-overlay"
                    role="status"
                    aria-live="polite"
                    data-testid="report-stale-overlay"
                  >
                    <span className="report-stale-badge">
                      {t('placeholder.updating', { label })}
                    </span>
                  </div>
                )}
                <div aria-busy={isStale} className="report-content-inner">
                  {report.hotspots.length === 0 ? (
                    <EmptyState label={label} />
                  ) : (
                    <>
                      {isPeriodInRewardDataGap(period) && (
                        <RewardDataGapNotice style={{ marginBottom: 4 }} />
                      )}
                      <ReportHero report={report} />
                      {/* F-R3-2 (Root Cause): recharts' ResponsiveContainer misst per
                          ResizeObserver (debounce 0) und schreibt das Ergebnis in den
                          State. Der Print-Reflow aendert die Containerbreite, was eine
                          Mess-/Render-Rueckkopplung ausloest und den Tab minutenlang
                          blockiert. Waehrend des Exports wird recharts daher komplett
                          UNMOUNTET und durch ein statisches SVG ersetzt. */}
                      {isPdfExporting ? (
                        <StaticDailyRewardsChart daily={report.daily} period={period} />
                      ) : (
                        <DailyRewardsChart
                          daily={report.daily}
                          period={period}
                          final={!isFuturePeriod(period) && period !== defaultPeriod()}
                        />
                      )}
                      <HotspotRewardTable
                        hotspots={report.hotspots}
                        currency={report.summary.currency}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="monthly-report-col-side">
            <div className="card card-pad report-export-card">
              <h3 className="section-heading" style={{ marginBottom: 10 }}>
                {t('exportCard.heading')}
              </h3>
              <CsvExportControls
                period={period}
                timeZone={timeZone}
                disabled={isPdfExporting}
                onDownloadingChange={setIsCsvDownloading}
              />
            </div>

            <div className="card card-pad report-export-card">
              <h3 className="section-heading" style={{ marginBottom: 10 }}>
                {t('taxYearly.heading')}
              </h3>
              <TaxYearlyExportControls />
            </div>

            <div className="card card-pad report-email-card">
              <h3 className="section-heading" style={{ marginBottom: 10 }}>
                {t('emailCard.heading')}
              </h3>
              <AutoEmailToggle />
              <div className="report-email-actions">
                <SendNowButton />
                <SendReportButton period={period} timeZone={timeZone} />
              </div>
            </div>

            {report && (
              <div className="card card-pad report-price-note">
                {/* Fix #120 (Nachbesserung): für den LAUFENDEN Monat gilt der
                    Live-Kurs, nicht der Monatsdurchschnitt (kein gültiger
                    Durchschnitt für einen angebrochenen Monat möglich, siehe
                    monthly_report_calculator.rb "PRICE-TRIO-FIX").
                    `period === defaultPeriod()` ist dieselbe Prüfung, die
                    diese Seite schon für die `final`-Badge auf
                    DailyRewardsChart nutzt.
                    BEKANNTE EINSCHRÄNKUNG (2. Nachbesserungsrunde, gemeldet
                    statt selbst "gefixt"): `defaultPeriod()` rechnet in
                    Browser-Lokalzeit (`new Date()`), das Backend entscheidet
                    dieselbe Frage in UTC (`Date.current`,
                    `config.time_zone = "UTC"`). Für Nutzer östlich von UTC
                    gibt es dadurch ein Fenster um Mitternacht UTC, in dem
                    diese Anzeige und die bestehende `final`-Badge bereits
                    "abgeschlossen" zeigen, während der Server serverseitig
                    noch den laufenden Monat berechnet. GEPRÜFT: Die
                    Monatsbericht-Antwort (MonthlyReportSerializer#as_json:
                    period/summary/hotspots/daily/generated_at) liefert KEIN
                    Endgültigkeits-Kennzeichen — es gibt nichts Serverseitiges,
                    das diese Anzeige stattdessen lesen könnte. Braucht eine
                    Backend-Ergänzung (z.B. `is_final`/`period_closed` im
                    Serializer); hier bewusst NICHT geraten/gebastelt. */}
                {period === defaultPeriod() ? (
                  <>
                    <strong>{t('priceNote.titleCurrentMonth')}</strong>{' '}
                    {t('priceNote.bodyCurrentMonth', {
                      rate: formatCurrency(
                        report.summary.avg_hnt_fiat_rate,
                        report.summary.currency,
                      ),
                      currency: report.summary.currency,
                    })}
                  </>
                ) : (
                  <>
                    <strong>{t('priceNote.titleClosedMonth')}</strong>{' '}
                    {t('priceNote.bodyClosedMonth', {
                      rate: formatCurrency(
                        report.summary.avg_hnt_fiat_rate,
                        report.summary.currency,
                      ),
                      currency: report.summary.currency,
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// --- Route Entry Point (verwendet URL-Search-Param) ---

type RouteProps = {
  period?: string
  navigate: (opts: { search: (prev: Record<string, unknown>) => Record<string, unknown> }) => void
}

export function MonthlyReportPageRoute({ period: rawPeriod, navigate }: RouteProps) {
  const period = rawPeriod && isValidPeriod(rawPeriod) ? rawPeriod : defaultPeriod()

  function handlePeriodChange(newPeriod: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(navigate as any)({
      search: (prev: Record<string, unknown>) => ({ ...prev, period: newPeriod }),
    })
  }

  return <MonthlyReportPage period={period} onPeriodChange={handlePeriodChange} />
}
